import { TEnumName, useEnum } from "@/api/services/global/enum";
import { useFormField } from "@/components/elements/FormField/FormField";
import { background, blue, button, divider, lightBlue, state, text } from "@/theme/colors";
import { typography } from "@/theme/typography";
import { rounded } from "@/theme/variables";
import { TOption, TRequiredOne } from "@/types/common";
import { TLanguageCode } from "@/types/enum";
import { convertFullWidthToHalfWidth } from "@/utils/pieces";
import { Autocomplete, Box, CircularProgress, FilterOptionsState, FormHelperText, MenuItem, TextField, css, styled } from "@mui/material";
import { FC, SyntheticEvent, useMemo, useState, useEffect } from "react";

export type TComboboxProps = {
  helperText?: string;
  placeholder?: string;
  nonControl?: boolean;
  loading?: boolean;
  disabled?: boolean;
  lang?: TLanguageCode;
  value?: string;
  disabledValues?: string[];
  isDisabledInput?: boolean;
  onChange?: (value: string) => void;
  /** Initial input value for the search field (useful for pre-filling scanned text) */
  initialInputValue?: string;
  onInputChange?: (value: string) => void;
} & TRequiredOne<{ options: TOption[]; enumName: TEnumName }>;

export const Combobox: FC<TComboboxProps> = ({
  nonControl = false,
  placeholder = "選択してください",
  helperText,
  options,
  enumName,
  lang,
  loading = false,
  disabled,
  disabledValues,
  isDisabledInput,
  initialInputValue,
  onInputChange,
  ...rest
}) => {
  const { field, fieldState } = useFormField(nonControl);
  const { data, isLoading } = useEnum(enumName, lang ? { lang } : undefined);
  const value = field ? field.value : rest.value;
  const [inputValue, setInputValue] = useState(initialInputValue ?? "");

  // Set initial input value when prop changes (handles async data loading)
  useEffect(() => {
    if (initialInputValue !== undefined && initialInputValue !== null) {
      setInputValue(initialInputValue);
    }
  }, [initialInputValue]);

  // Sync inputValue when external value is cleared
  useEffect(() => {
    if (!value) {
      setInputValue("");
    }
  }, [value]);

  const selectOptions = useMemo(() => options ?? data ?? [], [options, data]);

  const onChange = (e: SyntheticEvent<unknown>, option: unknown) => {
    const value = (option as TOption)?.value || null;
    rest.onChange?.(value as string);
    field?.onChange(value);
    field?.onBlur();
  };

  const selectedOption = useMemo(() => selectOptions.find((option) => option.value === value) || null, [selectOptions, value]);

  const isPrevious = useMemo(() => {
    if (fieldState?.isDirty === false) return true;
    return false;
  }, [fieldState?.isDirty]);

  return (
    <BoxWrapper position="relative">
      <StyledAutocomplete
        {...rest}
        {...field}
        isPrevious={isPrevious}
        noOptionsText="選択肢なし"
        disabled={disabled}
        error={Boolean(fieldState?.error)}
        value={selectedOption}
        inputValue={inputValue}
        onInputChange={(_, newInputValue, reason) => {
          // Handle different input change reasons:
          // - "input": user typing → always update
          // - "clear": user clicks clear button → always update (clear the field)
          // - "reset": MUI syncing with value prop → only update if not clearing (preserves initialInputValue)
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
          } else if (reason === "reset" && newInputValue !== "") {
            // Allow reset with non-empty value (e.g., when selecting an option)
            setInputValue(newInputValue);
            onInputChange?.(newInputValue);
          }
        }}
        getOptionLabel={(option) => (option as TOption).label}
        onChange={onChange}
        filterOptions={(options: unknown[], state: FilterOptionsState<unknown>) => {
          const filteredList =
            state.inputValue === ""
              ? options
              : options.filter((opt) => {
                  const label = convertFullWidthToHalfWidth((opt as TOption).label);
                  const query = convertFullWidthToHalfWidth(state.inputValue);
                  return label.includes(query) || label.toLowerCase().includes(query.toLowerCase());
                });
          return filteredList;
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder={placeholder}
            onBlur={(e) => {
              params.inputProps.onBlur?.(e as React.FocusEvent<HTMLInputElement>);

              if (!selectedOption) {
                setInputValue("");
                onInputChange?.("");
              }
            }}
          />
        )}
        options={selectOptions}
        renderOption={(props, option) => {
          const isDisable = "hidden" in (option as TOption) && (option as TOption).hidden === true;
          const isHidden = isDisable && value !== (option as TOption).value;
          if (isHidden) return <></>;
          return (
            <MenuItem
              {...props}
              key={(option as TOption).value}
              value={(option as TOption).value}
              disabled={isDisable || disabledValues?.includes((option as TOption)?.value?.toString())}
              title={(option as TOption).label}
            >
              <div style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{(option as TOption).label}</div>
            </MenuItem>
          );
        }}
        componentsProps={{
          paper: {
            sx: {
              fontSize: "14px !important",
              ".MuiMenuItem-root": typography.body14,
            },
          },
        }}
      />
      {(isLoading || loading) && (
        <LoadingWrap>
          <CircularProgress color="inherit" size={14} />
        </LoadingWrap>
      )}
      {Boolean(helperText) && <FormHelperText>{helperText}</FormHelperText>}
    </BoxWrapper>
  );
};

const BoxWrapper = styled(Box)``;

const options = { shouldForwardProp: (propName: string) => !["error", "isPrevious"].includes(propName) };

const StyledAutocomplete = styled(Autocomplete, options)<{ error: boolean; isPrevious: boolean }>`
  width: 100%;
  .MuiAutocomplete-inputRoot {
    padding: 0px !important;
    padding-right: 56px !important;
  }

  .MuiInputBase-root {
    position: relative;
    background: ${background.white};
    .MuiSelect-select {
      color: ${text.primary};
    }

    input::placeholder {
      color: ${text.primary};
    }
    fieldset {
      border-color: ${divider.middle};
      border-radius: ${rounded.xs};
      border-width: 1px !important;
    }
    &:hover,
    &.Mui-focused {
      fieldset {
        border-color: ${blue[70]};
      }
    }
    &.Mui-focused {
      &::after {
        content: "";
        position: absolute;
        top: -4px;
        left: -4px;
        width: 100%;
        height: 100%;
        padding: 2px;
        border: 2px solid ${button.secondary};
        box-sizing: content-box;
        border-radius: ${rounded.sm};
        z-index: -1;
      }
    }

    .MuiInputBase-input {
      padding: 6px 8px;
      ${css(typography.body14)};
      text-overflow: ellipsis;
      overflow: hidden;
      white-space: nowrap;
    }

    ${({ isPrevious }) =>
      isPrevious &&
      css`
        color: ${lightBlue[60]};
      `}

    ${({ error }) =>
      error &&
      css`
        background: ${background.error};
        fieldset {
          border-color: ${state.error_1}!important;
        }
      `};

    &.Mui-disabled {
      background: ${background.disable};
      fieldset {
        border-color: ${divider.middle}!important;
      }
    }
    .MuiSelect-icon {
      transform: none !important;
      color: ${text.primary};
    }
  }
`;

const LoadingWrap = styled(Box)`
  position: absolute;
  display: block;
  top: 0;
  right: 0;
  background-color: inherit;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 30px;
`;
